import type { ImageWithDate } from '../../../../types/contactLog';
import { pushDebugLog, formatError } from '../../../../lib/debugLog';

// s3_key またはURLからファイル名を組み立てる（拡張子は元を尊重、無ければ jpg）
export const buildFileName = (image: ImageWithDate): string => {
  const source = image.s3_key ?? image.url;
  const base = source.split('/').pop()?.split('?')[0];
  if (base && base.includes('.')) return base;
  const dateLabel = image.date ?? 'photo';
  return `photo_${dateLabel}.jpg`;
};

// デバッグログ用。URL が壊れていてもログ出力自体は落とさない
const hostOf = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return 'invalid-url';
  }
};

/** 取得の中断。共有シートのキャンセルも同じ AbortError なので判定を共有する */
export const isAbortError = (err: unknown): boolean =>
  err instanceof DOMException && err.name === 'AbortError';

// URL を fetch して File に変換
const toFile = async (image: ImageWithDate, signal?: AbortSignal): Promise<File> => {
  // cache: 'reload' は必須。同じ URL は先に <img> / background-image が CORS なしで
  // 読み込んでおり、その opaque レスポンスが HTTP キャッシュに載る。これを fetch が
  // 再利用すると CORS 評価に通らず TypeError で落ちる。
  // presigned URL は署名がクエリ文字列を含むため、キャッシュバスターの付与は使えない。
  const res = await fetch(image.url, { cache: 'reload', signal });
  // 404/403 等でも HTML やエラーペイロードを画像として保存しないよう、失敗は例外にする
  if (!res.ok) {
    throw new Error(`画像の取得に失敗しました (status: ${res.status})`);
  }
  const blob = await res.blob();
  const contentType = blob.type || res.headers.get('content-type') || '';
  // 画像以外（エラーページの text/html 等）を弾く。型不明（空）は従来どおり jpeg 扱い
  if (contentType && !contentType.startsWith('image/')) {
    throw new Error(`画像以外のレスポンスを受信しました (content-type: ${contentType})`);
  }
  return new File([blob], buildFileName(image), { type: contentType || 'image/jpeg' });
};

/**
 * 保存対象の画像を File 化して先読みする。
 *
 * navigator.share() は transient activation（ユーザー操作の有効期限）を要求するため、
 * 呼び出しまでに await を挟むと WebKit では NotAllowedError で reject される。
 * そのため画像の取得は保存ボタンのタップ前に完了させておく必要がある。
 *
 * cache: 'reload' により毎回フル通信になるため、不要になった取得は signal で中断する。
 *
 * @param signal 選択解除・写真切替・モーダル終了で取得を打ち切るためのシグナル
 */
export const prefetchFiles = async (
  images: ImageWithDate[],
  signal?: AbortSignal
): Promise<File[]> => {
  try {
    const files = await Promise.all(images.map((image) => toFile(image, signal)));
    pushDebugLog(
      `[写真保存] 先読み成功 cache=reload ${files.length}件 type=${files.map((f) => f.type).join(',')}`
    );
    return files;
  } catch (err) {
    // 中断は不要になった取得の打ち切りなので、失敗としては記録しない
    if (isAbortError(err)) throw err;
    // 配信元バケットごとに CORS 設定が異なるため、どのホストで失敗したかを残す。
    // presigned URL の署名を残さないよう host のみに絞る
    const hosts = [...new Set(images.map((image) => hostOf(image.url)))].join(',');
    pushDebugLog(`[写真保存] 先読み失敗 cache=reload ${formatError(err)} host=${hosts}`);
    throw err;
  }
};

/**
 * 共有シートでファイルを渡せる環境か。
 *
 * Android WebView は Web Share API 自体が未実装なため false になる。保存経路が変わると
 * 提示すべき UI も変わるため、先読み前の時点でも判定できるよう File を要求しない形にする。
 * 実際に渡せるかは保存時に `navigator.canShare({ files })` で再確認する。
 */
export const canUseShareSheet = (): boolean =>
  typeof navigator.share === 'function' && typeof navigator.canShare === 'function';

/** 保存経路の結果。呼び出し側は利用者へ出す案内と後片付けの判断に使う */
export type PhotoSaveOutcome =
  // 共有シートへ渡した（実際に保存したかは OS 側の操作なので分からない）
  | 'shared'
  // 利用者が共有シートを閉じてやめた。失敗ではないので選択や保存ボタンは維持する
  | 'canceled'
  // 長押し保存を案内する必要がある（1枚）
  | 'needsLongPress'
  // 1枚ずつ開いて長押し保存してもらう必要がある（複数枚）
  | 'needsOneByOne';

/**
 * 先読み済みの File を端末に保存する。
 *
 * IMPORTANT: navigator.share() へ到達するまで await を挟んではならない。transient activation
 * を失うと WebKit が NotAllowedError を返す。呼び出し側もタップハンドラから直接呼ぶこと。
 *
 * 共有シートが使えない場合（Android WebView は Web Share API 自体が未実装）と files が
 * null の場合（先読み失敗）は、LINE アプリ内ブラウザの長押し保存を案内する結果を返す。
 * 呼び出し側が案内 UI を出す責務を持つ。
 *
 * @param files 先読み済みの File。先読みに失敗した場合や共有シート非対応環境では null
 * @param images files に対応する画像
 */
export const savePrefetchedPhotos = async (
  files: File[] | null,
  images: ImageWithDate[]
): Promise<PhotoSaveOutcome> => {
  const hasFiles = Boolean(files && files.length > 0);
  // 共有シートに渡せるかは環境依存で、LIFF ブラウザは開発者ツールが使えないため判定結果を残す
  const canShareFiles = hasFiles ? navigator.canShare?.({ files: files as File[] }) : undefined;
  pushDebugLog(
    `[写真保存] share=${typeof navigator.share} canShare=${typeof navigator.canShare} ` +
      `files=${files ? files.length : 'null'} canShareFiles=${String(canShareFiles)}`
  );

  if (hasFiles && canShareFiles) {
    try {
      await navigator.share({ files: files as File[] });
      pushDebugLog('[写真保存] 共有シートへ渡した');
      return 'shared';
    } catch (err) {
      // キャンセルは失敗でも成功でもない。呼び出し側が選択を維持できるよう区別して返す
      if (isAbortError(err)) {
        pushDebugLog('[写真保存] 共有シートをキャンセル');
        return 'canceled';
      }
      console.error('共有シートでの保存に失敗しました', err);
      pushDebugLog(`[写真保存] 共有シート失敗 ${formatError(err)}`);
    }
  }

  // 長押し保存は1枚ずつしかできないため、複数枚は案内の文言を変える
  if (images.length !== 1) {
    pushDebugLog(`[写真保存] まとめて保存不可 ${images.length}件`);
    return 'needsOneByOne';
  }

  pushDebugLog('[写真保存] 長押し保存を案内');
  return 'needsLongPress';
};
